import { useContext, useEffect, useRef, useState } from "react";
import { apiContext } from '../../../../../contexts/apiContext';
import { notifyError, notifySuccess } from '../../../../../components/snackbar/snackbar';
import TextAreaField from '../../../../../components/textAreaField/textAreaField';
import { getMarkers, getReceiverGroups, saveNotificationReceiverGroups, saveNotificationTemplate } from "../../../../../services/administrationService/administrationService";
import { NotificationMarker, PortalGroup, PortalGroupResponse } from "../../../../../services/administrationService/types/administrationServiceTypes";
import ButtonField from "../../../../../components/buttonField/buttonField";
import { classNameBtn, iconPosition } from "../../../../../components/shared/button/types/buttonProps";
import { styleFormFieldHalf, stylizationVerticalAlign } from "../../../../../components/shared/stylization";
import { ButtonFieldProps } from "../../../../../components/buttonField/types/buttonFieldProps";
import { ReactComponent as notifyIcon } from '../../../../../icons/notifications.svg';
import SelectMarker from "../selectMarker/selectMarker";
import Modal from "../../../../../components/modalWindow/modal";
import { EditNotificationConfigurationProps } from "./types/editNotificationConfigurationProps";
import { Option } from "../../../../../components/shared/selectInput/types/selectInputProps";
import SelectInput from "../../../../../components/shared/selectInput/selectInput";
import Spinner from "../../../../../components/spinner/spinner";
import { ClassNameSpinner } from "../../../../../components/spinner/types/spinnerProps";

const EditNotificationConfiguration = (props: EditNotificationConfigurationProps) => {
    const api = useContext(apiContext);
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [markers, setMarkers] = useState<NotificationMarker[]>([]);
    const [topic, setTopic] = useState<string>(props.row.temaT_SZABLON ? props.row.temaT_SZABLON : '');
    const [content, setContent] = useState<string>(props.row.tresC_SZABLON ? props.row.tresC_SZABLON : '');
    const [allReceiver, setAllReceiver] = useState<Option[]>([]);
    const [receiver, setReceivers] = useState<Option[]>([]);
    const [activeTab, setActiveTab] = useState<string>("T");
    const [reload, setReload] = useState<boolean>(true);
    const [disabledTopic, setDisabledTopic] = useState<boolean>(!props.row.temaT_SZABLON);
    const [disabledContent, setDisabledConetent] = useState<boolean>(!props.row.tresC_SZABLON);
    const changedRef = useRef(false);

    useEffect(() => {
        setLoading(true);
        const category = props.row.kategoriA_POWIADOMIENIA_DOM;
        const id = props.row.id;
        const url = api.apiUrl + api.apiVersion;
        if (category) {
            const promiseArray = [getMarkers(url, category), getReceiverGroups(url, id)];
            Promise.all(promiseArray).then((res) => {
                setMarkers(res[0] as NotificationMarker[]);
                const receiverResponse = res[1] as PortalGroupResponse;
                setAllReceiver(toOption(receiverResponse.dic));
                setReceivers(toOption(receiverResponse.features));
                setLoading(false);
            }).catch(err => {
                console.error(err);
                notifyError({ message: "Nie udało się pobrać danych do edycji konfiguracji" });
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
        return () => { }
    }, [api.apiUrl, api.apiVersion, props.row.id, props.row.kategoriA_POWIADOMIENIA_DOM, reload]);

    const toOption = (arry?: PortalGroup[]) => {
        if (!arry) return [];
        return arry.map(a => { return { name: a.nazwa, value: a.id } });
    }

    const save = () => {
        changedRef.current = true;
        if (activeTab === 'T') {
            saveTemplate();
        } else {
            saveReceivers();
        }
    }

    const saveTemplate = () => {
        let saveObjet = { ...props.row, temaT_SZABLON: topic, tresC_SZABLON: content };
        setDisabled(true);
        saveNotificationTemplate(api.apiUrl + api.apiVersion, saveObjet).then((data: any) => {
            notifySuccess({ message: "Zaktualizowano konfigurację powiadomienia" });
            setDisabled(false);
            setReload(!reload);
        }).catch((error: any) => {
            notifyError({ message: "Aktualizacja konfiguracji powiadomienia zakończona niepowodzeniem" });
            setDisabled(false);
        });
    }

    const saveReceivers = () => {
        const templateId = props.row.id;
        const receiverIds = receiver.map(r => r.value as number);
        setDisabled(true);
        saveNotificationReceiverGroups(api.apiUrl + api.apiVersion, templateId, receiverIds).then((data: any) => {
            notifySuccess({ message: "Zaktualizowano listę adresatów" });
            setDisabled(false);
            setReload(!reload);
        }).catch((error: any) => {
            notifyError({ message: "Aktualizacja listy adresatów zakończona niepowodzeniem" });
            setDisabled(false);
        });
    }

    const getAreaProps = (label: string, placeholder: string, value: string, callback: any, disabledCallback: any) => {
        return {
            labelName: label,
            textAreaPlaceholder: placeholder,
            value: value,
            textareaSharedProps: {
                className: 'body-txt-large',
                style: { width: 'calc(100%-20px)', height: '120px' }
            },
            configValidation: { required: true },
            disabled: false,
            pristine: true,
            onFill: (isFill: boolean) => {
                disabledCallback(!isFill);
            },
            setValue: (value: any) => {
                callback(value);
            }
        }
    }

    const getBtnProps = (): ButtonFieldProps => {
        const _disabled = activeTab === 'T' ? disabledContent || disabledTopic || disabled : disabled;
        return {
            BtnProps: {
                onClick: save,
                className: `${classNameBtn.Btn} ${classNameBtn.Large} body-txt-medium`,
                disabled: _disabled
            },
            placeHolder: "Zapisz",
            placeHolderStyle: stylizationVerticalAlign,
            iconPosition: iconPosition.Start
        };
    }

    const getViewBtnProps = (type: string, title: string, onClick?: React.MouseEventHandler<HTMLButtonElement>): ButtonFieldProps => {
        let className = classNameBtn.BigSecondary + " body-txt-XL";
        if (activeTab === type) {
            className += " BigSecondaryActive";
        }
        return {
            BtnProps: {
                onClick: (e) => {
                    setActiveTab(type);
                    if (onClick) onClick(e);
                },
                disabled: disabled,
                style: { width: '310px', height: '86px' },
                className: className
            },
            placeHolder: title,
            placeHolderStyle: stylizationVerticalAlign
        };
    }

    const handleTopic = (value: string) => {
        setTopic(topic + " $" + value);
        setDisabledTopic(false);
    }

    const handleContent = (value: string) => {
        setContent(content + " $" + value);
        setDisabledConetent(false);
    }

    const handleChangeReceivers = (opt: Option[] | undefined) => {
        setReceivers(opt ? opt : []);
    }

    const handleClose = () => {
        props.onClose(changedRef.current);
    }

    return (
        <Modal title={"Konfiguracja powiadomień"}
            titleIcon={notifyIcon}
            width={'640px'}
            height={'90vh'}
            actionBtns={[<ButtonField key={1} {...getBtnProps()} />]}
            onClose={handleClose}>
            <div className="editNotificationConfigurationView">
                {loading ? <Spinner spinnerClass={ClassNameSpinner.Big} /> : <>
                    <div className="notify-configuration-tabs">
                        <ButtonField {...getViewBtnProps("T", "Edytuj treść")}></ButtonField>
                        <ButtonField {...getViewBtnProps("A", "Edytuj adresatów")}></ButtonField>
                    </div>
                    <div className="notify-configuration-content">
                        {activeTab === 'T' && <>
                            <div className="notify-configuration-row">
                                <TextAreaField {...getAreaProps('Tytuł powiadomienia', 'Dodaj tytuł powiadomienia', topic, setTopic, setDisabledTopic)} />
                                <SelectMarker markers={markers} title={"Znaczniki dla tytułu"} onChange={handleTopic}></SelectMarker>
                            </div>
                            <div className="notify-configuration-row">
                                <TextAreaField {...getAreaProps('Treść powiadomienia', 'Dodaj treść powiadomienia', content, setContent, setDisabledConetent)} />
                                <SelectMarker markers={markers} title={"Znaczniki dla treści"} onChange={handleContent}></SelectMarker>
                            </div>
                        </>}
                        {activeTab === 'A' && <div className="notify-configuration-row">
                            <SelectInput style={styleFormFieldHalf} label="Adresaci"
                                hasInputTags={true} options={allReceiver} onlySingleTag={false}
                                selectedOptions={receiver} onChange={handleChangeReceivers}
                            />
                        </div>}
                    </div>
                </>}
            </div>
        </Modal>
    )
};

export default EditNotificationConfiguration;